import React from 'react';
import styled from '@emotion/styled';
import get from 'lodash/get';
import { COLOR, FONT_SIZE, FONT_WEIGHT } from '@ordermentum/ui-constants';
import { SHADOW, PADDING } from '@ordermentum/ui-static';

type WrapperProps = {
  hasError?: boolean;
  size?: sizeOptions;
  customTheme: 'standard' | 'flat-slate';
};

const height = (p: WrapperProps) => {
  switch (p.customTheme) {
    case 'flat-slate':
      return {
        sml: '2rem',
        medium: '3.1rem',
        lrg: '3.5rem',
        large: '3.5rem',
      }[p.size || 'sml'];
    case 'standard':
    default:
      return {
        sml: '35px',
        medium: '35px',
        lrg: '50px',
        large: '50px',
      }[p.size || 'sml'];
  }
};

const afterTop = (p: WrapperProps) => {
  switch (p.customTheme) {
    case 'flat-slate':
      return {
        // rem ftw!!!
        sml: '0.633334375rem',
        medium: '1.183334375rem',
        lrg: '1.383334375rem',
        large: '1.383334375rem',
      }[p.size || 'sml'];
    case 'standard':
    default:
      return {
        sml: '10px',
        medium: '10px',
        lrg: '18px',
        large: '18px',
      }[p.size || 'sml'];
  }
};

const background = (p: WrapperProps) => {
  switch (p.customTheme) {
    case 'flat-slate':
      return COLOR.GRAY_FAFAFA;
    case 'standard':
    default:
      return p.hasError ? COLOR.RED_100 : COLOR.WHITE;
  }
};

const borderRadius = (p: WrapperProps) => {
  switch (p.customTheme) {
    case 'flat-slate':
      return '6px';
    case 'standard':
    default:
      return '2px';
  }
};

const boxShaddow = (p: WrapperProps) => {
  switch (p.customTheme) {
    case 'flat-slate':
      return 'none';
    case 'standard':
    default:
      return SHADOW.INPUT;
  }
};

const border = (p: WrapperProps) => {
  switch (p.customTheme) {
    case 'flat-slate':
      return `1px solid ${COLOR.GRAY_DEDEDE}`;
    case 'standard':
    default:
      return p.hasError
        ? `1px solid ${COLOR.ERROR}`
        : `1px solid ${COLOR.GRAY_400}`;
  }
};

const fontWeight = (p: WrapperProps) => {
  switch (p.customTheme) {
    case 'flat-slate':
      return FONT_WEIGHT.LIGHT;
    case 'standard':
    default:
      return FONT_WEIGHT.NORMAL;
  }
};

const color = (p: WrapperProps) => {
  switch (p.customTheme) {
    case 'flat-slate':
      return COLOR.BLACK;
    case 'standard':
    default:
      return COLOR.GRAY_600;
  }
};

const generalSelectStyles = (p: WrapperProps) => {
  switch (p.customTheme) {
    case 'flat-slate':
      return `
      /* https://stackoverflow.com/a/58783503/1112586 */
      @supports (-ms-ime-align:auto) {
        /* Edge adds it's own padding for some reason.. */
        padding-left: calc(${PADDING.INPUT_LR} - 3px);
      }
      @-moz-document url-prefix() {
        /* Firefox adds it's own padding for some reason... */
        padding-left: calc(${PADDING.INPUT_LR} - 4px);
      }
    `;
    case 'standard':
    default:
      return '';
  }
};

const Wrapper = styled('div')<WrapperProps>`
  background: ${background};
  border: none;
  border-radius: ${borderRadius};
  position: relative;
  width: 100%;

  &::after {
    content: '';
    border-bottom: 1px solid ${COLOR.GRAY_600};
    border-right: 1px solid ${COLOR.GRAY_600};
    height: 9px;
    position: absolute;
    right: 10px;
    top: ${afterTop};
    transform: rotate(45deg);
    width: 9px;
  }

  select {
    appearance: none;
    background: none;
    border: none;
    border: ${border};
    border-radius: ${borderRadius};
    color: ${color};
    font-size: ${FONT_SIZE.SM};
    font-weight: ${fontWeight};
    height: ${height};
    padding: 0 ${PADDING.INPUT_LR};
    width: 100%;
    box-shadow: ${boxShaddow};
    ${generalSelectStyles};
    min-width: 4rem;
  }

  select:focus {
    outline: -webkit-focus-ring-color auto 5px;
    outline-offset: -2px;
  }
`;

export type SelectOption = {
  tag: string;
  display: string;
};

// duplicating lrg and large because of how it is used on other components
type sizeOptions = 'sml' | 'medium' | 'lrg' | 'large';

type Props = {
  options: SelectOption[];
  placeholder?: string;
  name: string;
  value?: string;
  disabled?: boolean;
  /** Function called whenever field is updated.
   *
   * @argument inputName name of this component (from the name prop)
   * @argument inputValue value this component was set to (from tag in the options prop)
   *
   * Designed for use with the formik.setFieldValue() method.
   */
  onChange: (inputName: string, inputValue: string) => void;
  onBlur?: (inputName: string, inputValue: string) => void;
  error?: boolean;
  size?: sizeOptions;
  theme?: 'standard' | 'flat-slate';
};

const Select = (props: Props) => {
  const {
    name,
    disabled,
    value,
    onChange,
    options,
    error,
    placeholder,
    size,
    theme = 'standard',
    onBlur,
  } = props;

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const inputName = get(e, 'target.name', '');
    const inputValue = get(e, 'target.value', '');
    onChange(inputName, inputValue);
  };

  const handleBlur = (e: React.FocusEvent<HTMLSelectElement>) => {
    if (onBlur) {
      const inputName = get(e, 'target.name', '');
      const inputValue = get(e, 'target.value', '');
      onBlur(inputName, inputValue);
    }
  };

  return (
    <Wrapper size={size} hasError={error} customTheme={theme}>
      <select
        id={name}
        name={name}
        disabled={disabled}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.length > 0 &&
          options.map((s, index) => (
            <option key={`option-${name}-${s.tag}`} value={s.tag}>
              {s.display}
            </option>
          ))}
      </select>
    </Wrapper>
  );
};

export default Select;
